import React, { useContext } from "react";
import { useQuery } from "@apollo/react-hooks";
import Header from "../../../components/Header";
import BackLink from "../../../components/BackLink";
import { AuthContext } from "../../../services/user/AuthProvider";
import { GET_FRANCHISE_REVENUE_TIER } from "../services/graphql";
import styles from "./FranchiseRevenue.module.scss";

const FranchiseRevenue = ({ franchiseId }) => {
  const { currentUser } = useContext(AuthContext);
  const { data, loading, error } = useQuery(GET_FRANCHISE_REVENUE_TIER, {
    variables: { franchiseId: decodeURIComponent(franchiseId) },
    fetchPolicy: "network-only"
  });
  const franchise = data && data.franchise;
  const tier = franchise && franchise.revenueTier;
  const progress = tier ? Math.max(0, Math.min(100, tier.progressPercentage)) : 0;
  const recentSchools = (franchise && franchise.recentQualifyingSchools) || [];

  return (
    <>
      <Header user={currentUser} portalFranchise={franchise} portalFranchiseId={franchiseId} portalPage="revenue" />
      <div className={styles.revenue}>
        <BackLink text="Dashboard" to={`/franchise-portal/${franchiseId}`} />
        {loading && <p role="status">Loading revenue progress...</p>}
        {error && <p role="alert">Unable to load revenue progress. Please try again later.</p>}
        {!loading && !error && !franchise && <p>Franchise not found or access unavailable.</p>}
        {!loading && !error && franchise && <>
          <div className={styles.heading}>
            <p className={styles.territory}>Franchise · {franchise.name}</p>
            <h1>Revenue tier progress</h1>
            <p>Track the lifetime school total and see what is needed to unlock the next commission level.</p>
          </div>

          <section className={styles.currentBand} aria-labelledby="current-commission-band">
            <h2 id="current-commission-band">Current commission band</h2>
            <div className={styles.currentSummary}>
              <strong>{tier.percentage}%</strong>
              <h3>{tier.lifetimeSchools.toLocaleString("en-GB")} qualifying schools</h3>
            </div>
            <p>{tier.nextTierPercentage !== null
              ? `Bring on ${tier.remainingToNext.toLocaleString("en-GB")} more schools to reach the ${tier.nextTierPercentage}% band.`
              : "You have reached the highest commission band."}</p>
            <div className={styles.progress} role="progressbar" aria-label="Commission tier progress"
              aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
              <span style={{ width: `${progress}%` }} />
            </div>
          </section>

          <section className={styles.section}>
            <h2>Commission levels</h2>
            <div className={styles.levels}>
              {franchise.commissionLadder.map(band => (
                <div className={styles.level} key={band.level} data-status={band.status} data-next={band.level === tier.level + 1}>
                  <h3>Level {band.level}</h3>
                  <strong>{band.percentage}%</strong>
                  <p className={styles.range}>{band.level === 3 ? "School 100 onwards" : `Schools ${band.range.replace(/ schools$/, "").replace("-", "–")}`}</p>
                  <p className={styles.levelStatus}>{band.status === "current" ? "Current"
                    : band.status === "completed" ? "Completed"
                      : `${band.remainingToNext.toLocaleString("en-GB")} more to enter`}</p>
                </div>
              ))}
            </div>
          </section>

          <section className={styles.section}>
            <h2>How your qualifying total works</h2>
            <ol className={styles.rules}>
              <li>A school is counted when its first paid annual subscription is activated.</li>
              <li>The count is lifetime: a cancellation after year one does not reduce your tier progress.</li>
              <li>Commission is generated only when a new or renewed subscription fee is paid.</li>
            </ol>
          </section>

          <section className={styles.section}>
            <h2>Recently added to your qualifying total</h2>
            <ul className={styles.recentSchools}>
              {recentSchools.length === 0 && <li>No qualifying schools yet.</li>}
              {recentSchools.map(school => (
                <li key={school.id}>
                  <strong>{school.name}</strong>
                  <span>{school.qualifyingDateFormatted || "—"}</span>
                </li>
              ))}
            </ul>
          </section>
        </>}
      </div>
    </>
  );
};

export default FranchiseRevenue;
